import { Component, OnInit, Input} from '@angular/core';
import { DatePipe } from '@angular/common';
import { Router, NavigationStart } from '@angular/router';
import { forkJoin, Observable, Subscription, of, combineLatest } from "rxjs";
import { tap, map, filter, mergeMap, catchError, switchMap } from "rxjs/operators";

//import { GridOptions, ColDef, AllModules } from '@ag-grid-enterprise/all-modules';
import { GridOptions, ColDef, GridApi } from 'ag-grid-community';

import { GroupService, GroupSearchSpec } from "../../../../admin/services/group.service";
import { Group } from "../../../../admin/models/group.model";
import { ContactStub, Contact } from "../../../../admin/models/contact.model";
import { PendingFlow } from '../../../../flow/models/flow.model';
import { WorkOrderService } from '../../../../common/services/work-order.service';
import { ModalInfoComponent } from "../../../../common/components/ag-grid/modal-info.component";

import { RouterLinkRendererComponent } from '../../../../common/components/ag-grid/router-link-renderer.component';
import { UserIdentity } from '../../../../models/user-identity.model';
import { AuthService } from '../../../../services/auth.service';
import { OrganizationStub } from '../../../../common/models/organization.model';
import { WorkOrderType, PendingWorkOrderAssignment } from '../../../../common/models/work-order.model';
import { FlowInstanceService } from '../../../../flow/services/flow-instance.service';
import { FlowIndex } from '../../../../common/models/flow.dto';
import { DateService } from '../../../../common/services/date.service';
import { dateComparator } from '../../../../common/components/data-table/comparators/data-table.comparators';
import { LunaClaimValues } from "../../../../models/user-identity.model";
import { GridFormatterService } from "../../../../common/services/grid-formatter.service";
import { PaymentService } from "../../../../payments/services/payment.service";
import { ApplicationPayment, Payment, PaymentStatus, PaymentSearchCriteria, PaymentInstrument, writeToken } from "../../../../payments/models/paymentItem.model";
import { IconComponent } from "../../../../common/components/data-table/columns/icon/icon.component";
import { ViewBase } from "../view-base";
import { MetadataSearchSpec, ApplicationType, ApplicationState } from "../../../../applications/models/applications.models";
import { ApplicationsService } from "../../../../applications/services/applications.service";
import { SystemSettingsService } from "../../../../admin/services/system-settings.service";
import { SystemSettingOverTime, SystemSettingSearchSpec, SettingNames } from "../../../../admin/models/system-setting.model";

@Component({
    selector: 'permits-widget',
    templateUrl: './permits-widget.component.html',
    styleUrls: ['./permits-widget.component.scss'],
    standalone: false
})
export class PermitsWidgetComponent extends ViewBase implements OnInit {
    @Input() widgetTitle: string;

    allUsers: ContactStub[] = [];
    groups: Group[] = [];
    level1FilteredUsers: ContactStub[] = [];
    level2FilteredUsers: ContactStub[] = [];
    level3FilteredUsers: ContactStub[] = [];
	adHocReviewGridOptions: GridOptions;
	adHocReviewGridApi?: GridApi;
	firstLevelReviewGridOptions: GridOptions;
	firstLevelReviewGridApi?: GridApi;
	incompleteGridOptions: GridOptions;
	incompleteGridApi?: GridApi;
    orgStubs: OrganizationStub[];
    pendingDenialData: FlowIndex[];
	pendingDenialGridOptions: GridOptions;
	pendingDenialGridApi?: GridApi;
    subscriptions: Subscription = new Subscription();
    incompletePermitApplications: FlowIndex[];
    payments: ApplicationPayment[];
    permitAdHocReviewPendingFlows: PendingFlow[];
    permitFirstLevelPendingFlows: PendingFlow[]
    permitSecondLevelPendingFlows: PendingFlow[];
    permitThirdLevelPendingFlows: PendingFlow[];
    returnedData: FlowIndex[];
	returnedGridOptions: GridOptions;
	returnedGridApi?: GridApi;
	secondLevelReviewGridOptions: GridOptions;
	secondLevelReviewGridApi?: GridApi;
    selectedItem: any;
    showingDetail: boolean = false;
	thirdLevelReviewGridOptions: GridOptions;  
	thirdLevelReviewGridApi?: GridApi;

	private expediteSettings: SystemSettingOverTime[] = [];

    applicationCompleteDateColumn: ColDef = {
        headerClass: "wrapped",
        editable: false,
        headerName: 'Application Complete Date',
        headerTooltip: 'Application Complete Date',
        field: 'applicationCompleteDate',
        tooltipValueGetter: this.gridFormatter.formatDate('applicationCompleteDate', DateService.DateFormat, 'N/A'),
        valueFormatter: this.gridFormatter.formatDate('applicationCompleteDate', DateService.DateFormat, 'N/A'),
        comparator: dateComparator,
        cellClass: this.almostDueClass(this),
        maxWidth: 135,
        minWidth: 130,
        filter: "agDateColumnFilter",
        filterParams: this.gridFormatter.defaultDateFilterParams
    } as ColDef;


    appNumberColumn: ColDef = {
        headerClass: "wrapped",
        editable: false,
        headerName: "Application #",
        headerTooltip: "Application #",
        field: "applicationNumber",
        tooltipField: "applicationNumber",
        minWidth: 145,
        maxWidth: 150,
        cellRenderer: RouterLinkRendererComponent,
        cellRendererParams: { textField: "applicationNumber" }        
    }

    appTypeColumn: ColDef = {
        headerClass: "wrapped",
        editable: false,
        headerName: 'Application Type',
        headerTooltip: 'Application Type',
        field: 'name',
        tooltipField: 'name',
        cellClass: this.almostDueClass(this),
        maxWidth: 200,
    } as ColDef;

    companyColumn: ColDef = {
        headerClass: "wrapped",
        editable: false,
        headerName: 'Company',
        headerTooltip: 'Company',
        field: 'companyName',
        tooltipField: 'companyName',
        cellClass: this.almostDueClass(this),
        maxWidth: 150,
    } as ColDef;

    dueDateColumn: ColDef = {
        headerClass: "wrapped",
        editable: false,
        headerName: 'Due',
        headerTooltip: 'Due',
        field: 'applicationDueDate',
        tooltipValueGetter: this.dateFormatter(this, 'applicationDueDate'),
        valueFormatter: this.dateFormatter(this, 'applicationDueDate'),
        comparator: dateComparator,
        cellClass: this.almostDueClass(this),
        maxWidth: 125,
        filter: "agDateColumnFilter",
        filterParams: this.gridFormatter.defaultDateFilterParams
    } as ColDef;

    paymentReceivedDateColumn: ColDef = {
        headerClass: "wrapped",
        editable: false,
        headerName: 'Payment Received Date',
        headerTooltip: 'Payment Received Date',
        field: 'paymentReceivedDate',
        tooltipValueGetter: this.gridFormatter.formatDate('paymentReceivedDate', DateService.DateFormat, 'N/A'),
        valueFormatter: this.gridFormatter.formatDate('paymentReceivedDate', DateService.DateFormat, 'N/A'),
        comparator: dateComparator,
        cellClass: this.almostDueClass(this),
        maxWidth: 125,
        filter: "agDateColumnFilter",
        filterParams: this.gridFormatter.defaultDateFilterParams
    } as ColDef;

    submittedColumn: ColDef = {
        headerClass: "wrapped",
        editable: false,
        headerName: 'Submitted',
        headerTooltip: 'Submitted',
        field: 'dateSubmitted',
        tooltipValueGetter: this.dateFormatter(this, 'dateSubmitted'),
        valueFormatter: this.dateFormatter(this, 'dateSubmitted'),
        comparator: dateComparator,
        cellClass: this.createdCellClassName(this),
        maxWidth: 125,
        filter: "agDateColumnFilter",
        filterParams: this.gridFormatter.defaultDateFilterParams
    } as ColDef;

    wellNameColumn: ColDef = {
        headerClass: "wrapped",
        editable: false,
        headerName: "Well Name",
        headerTooltip: "Well Name",
        valueGetter: this.gridFormatter.formatPotentiallyBlankField("wellName", "N/A"),
        tooltipValueGetter: this.gridFormatter.formatPotentiallyBlankField("wellName", "N/A"),
        minWidth: 125,
        maxWidth: 200,
        filter: "agTextColumnFilter",
        resizable: true,
    } as ColDef;


    wellNumberColumn: ColDef = {
        headerClass: "wrapped",
        editable: false,
        headerName: "Well Number",
        headerTooltip: "Well Number",
        valueGetter: this.gridFormatter.formatPotentiallyBlankField("wellNumber", "N/A"),
        tooltipValueGetter: this.gridFormatter.formatPotentiallyBlankField("wellNumber", "N/A"),
        minWidth: 125,
        maxWidth: 200,
        filter: "agTextColumnFilter",
        resizable: true,
    } as ColDef;

    public columnDefs: ColDef[] = [
        this.submittedColumn,
        this.dueDateColumn,
        this.appNumberColumn,
        this.companyColumn,
        this.wellNameColumn,
        this.wellNumberColumn,
        this.appTypeColumn
    ];

    pendingAssignments: PendingWorkOrderAssignment[] = [];

    icons = new Map(
        [
            ["Paid", "icon-dollar"],
            ["Unpaid", ""]
        ]
    );

    public iconPaidColumn: ColDef = {
        headerClass: "wrapped",
        editable: false,
        headerName: "",
        cellRenderer: IconComponent,
        cellRendererParams: { icons: this.icons, class: 'green' },
        cellStyle: { 'color': '#00FF00' },
        valueGetter: this.paidFieldFormatter(this),
        tooltipValueGetter: this.paidFieldFormatter(this),
        maxWidth: 30,
        sortingOrder: ["asc", "desc", null]
    } 

    public pendingDenialColumns: ColDef[] = [
        {
            headerClass: "wrapped",
            editable: false,
            headerName: "Application #",
            headerTooltip: "Application #",
            field: 'applicationNumber',
            tooltipField: 'applicationNumber',
            cellRenderer: RouterLinkRendererComponent,
            cellRendererParams: {
                enableLink: this.enableFlowIndexApplicationNumberLink(),
                textField: 'applicationNumber',
                flowIdField: 'flowId',
                workItemIdField: 'currentWorkItemId'
            },
            maxWidth: 200
        },
        { headerClass: "wrapped", headerName: 'Company Name', headerTooltip: 'Company Name', editable: false, field: 'companyName', tooltipField: 'companyName', minWidth: 140 },
        this.wellNameColumn,
        this.wellNumberColumn,
        { headerClass: "wrapped", headerName: 'Status', headerTooltip: 'Status', editable: false, field: 'state', tooltipField: 'state', maxWidth: 120 },
        {
            headerClass: "wrapped",
            editable: false,
            headerName: 'Submitted Date',
            headerTooltip: 'Submitted Date',
            field: 'dateSubmitted',
            tooltipValueGetter: this.dateFormatter(this, 'dateSubmitted'),
            valueGetter: this.dateFormatter(this, 'dateSubmitted'),
            comparator: dateComparator,
            maxWidth: 120,
            filter: "agDateColumnFilter",
            filterParams: this.gridFormatter.defaultDateFilterParams
        },
        {
            headerClass: "wrapped",
            editable: false,
            headerName: 'Due',
            headerTooltip: 'Due',
            field: 'applicationDueDate',
            tooltipValueGetter: this.dateFormatter(this, 'applicationDueDate'),
            valueGetter: this.dateFormatter(this, 'applicationDueDate'),
            comparator: dateComparator,
            maxWidth: 120,
            filter: "agDateColumnFilter",
            filterParams: this.gridFormatter.defaultDateFilterParams
        },
        { headerClass: "wrapped", headerName: 'Application Type', headerTooltip: 'Application Type', editable: false, field: 'name', tooltipField: 'name', maxWidth: 140 },

        { headerClass: "wrapped", headerName: 'API Number', headerTooltip: 'API Number', editable: false, field: 'apiNumber', tooltipField: 'apiNumber', maxWidth: 120 },
        { headerClass: "wrapped",headerName: 'Expedited', headerTooltip: 'Expedited', editable: false, field: 'isExpedited', tooltipField: 'isExpedited', maxWidth: 125 },
        { headerClass: "wrapped",headerName: 'Urban', headerTooltip: 'Urban', editable: false, field: 'isUrban', tooltipField: 'isUrban', maxWidth: 100 }
    ];

    public submittedIncompleteApplicationsColumns: ColDef[] = [
        this.iconPaidColumn,
        {
            headerClass: "wrapped",
            editable: false,
            headerName: 'Submitted',
            headerTooltip: 'Submitted',
            field: 'dateSubmitted',
            tooltipValueGetter: this.dateFormatter(this, 'dateSubmitted'),
            valueGetter: this.dateFormatter(this, 'dateSubmitted'),
            comparator: dateComparator,
            cellClass: this.createdCellClassName(this),
            resizable: true,
            maxWidth: 125
        },
        {
            cellRendererSelector: (params) => {
                if (params.data.targetUrl != null) {
                    return {
                        component: RouterLinkRendererComponent,
                        params: {
                            openInNewWindow: false,
                            text: params.data.applicationNumber,
                            targetUrl: params.data.targetUrl
                        }
                    };
                } else {
                    return {
                        component: RouterLinkRendererComponent,
                        params: {
                            openInNewWindow: false,
                            textField: "applicationNumber",
                            url: (data: FlowIndex) => {
                                return `/flow/${data.flowId}/${data.currentWorkItemId}`;
                            }
                        }
                    };
                }
               
            },
            headerClass: "wrapped",
            editable: false,
            headerName: "Application #",
            headerTooltip: "Application #",
            field: 'applicationNumber',
            tooltipField: 'applicationNumber',
            resizable: true,
            maxWidth: 140
        },
        { headerClass: "wrapped", headerName: 'Company Name', headerTooltip: 'Company Name', editable: false, field: 'companyName', tooltipField: 'companyName', resizable: true, minWidth: 140 },
        this.wellNameColumn,
        this.wellNumberColumn,
        { headerClass: "wrapped", headerName: 'Application Type', headerTooltip: 'Application Type', editable: false, field: 'name', tooltipField: 'name', resizable: true, minWidth: 140 },
        this.applicationCompleteDateColumn,
        this.paymentReceivedDateColumn
    ];

    public returnedPermitApplicationsColumns: ColDef[] =  [
        {
            headerClass: "wrapped",
            editable: false,
            headerName: 'Submitted',
            headerTooltip: 'Submitted',
            field: 'dateSubmitted',
            tooltipValueGetter: this.dateFormatter(this, 'dateSubmitted'),
            valueGetter: this.dateFormatter(this, 'dateSubmitted'),
            comparator: dateComparator,
            cellClass: this.createdCellClassName(this),
            resizable: true,
            maxWidth: 125
        },
        {
            headerClass: "wrapped",
            editable: false,
            headerName: 'Due',
            headerTooltip: 'Due',
            field: 'applicationDueDate',
            tooltipValueGetter: this.dateFormatter(this, 'applicationDueDate'),
            valueGetter: this.dateFormatter(this, 'applicationDueDate'),
            comparator: dateComparator,
            resizable: true,
            maxWidth: 125
        },
        {
            headerClass: "wrapped",
            cellRenderer: RouterLinkRendererComponent,
            cellRendererParams: {
                openInNewWindow: false,
                textField: "applicationNumber",
                url: (data: FlowIndex) => {
                    const prefix = (data.onBehalf)
                        ? "/flow/review-on-behalf"
                        : (data.name != 'Stratigraphic')
                            ? "/flow/review"
                            : "/applications/stratigraphic/readonly";
                    return `${prefix}/${data.flowId}`;
                }
            },
            editable: false,
            headerName: "Application #",
            headerTooltip: "Application #",
            field: 'applicationNumber',
            tooltipField: 'applicationNumber',
            resizable: true,
            maxWidth: 140
        },
        { headerClass: "wrapped", headerName: 'Company Name', headerTooltip: 'Company Name', editable: false, field: 'companyName', tooltipField: 'companyName', resizable: true, minWidth: 140, maxWidth: 175 },
        this.wellNameColumn,
        this.wellNumberColumn,
        { headerClass: "wrapped", headerName: 'Application Type', headerTooltip: 'Application Type', editable: false, field: 'name', tooltipField: 'name', resizable: true, minWidth: 140, maxWidth: 175 }
    ];

   

    constructor(
        public workOrderService: WorkOrderService,
        public authService: AuthService,
        public datePipe: DatePipe,
        public flowInstanceService: FlowInstanceService,
        private router: Router,
        public dateService: DateService,
        public groupService: GroupService,
        private gridFormatter: GridFormatterService,
        private paymentService: PaymentService,
		private applicationService: ApplicationsService,
		private _settings: SystemSettingsService
    ) {
		super("ViewPermitsWidget", [LunaClaimValues.ViewPermitsWidget]);
    }

    ngOnInit() {
        this.refreshData();
        this.setGridOptions();
        this.router.events.pipe(filter(e => e instanceof NavigationStart)).subscribe((routerEvent: NavigationStart) => {
            this.subscriptions.unsubscribe();
        });
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    assign(params: any): any {
        let result: boolean = false;
        if (params && params.newValue && params.newValue.id) {
            if (params.newValue.id != "-1") {
                params.data.assignedUserId = params.newValue.id;
            } else {
                params.data.assignedUserId = "";
            }
            
            if (params.context && params.context.componentParent) {
                let parent = <PermitsWidgetComponent>params.context.componentParent;
                let selectedItem = parent.allUsers.find(u => u.id == params.newValue.id);
                if (selectedItem) {
                    params.data.assignedUserName = selectedItem.displayName;
                }
            }
            result = true;
        }        
        return result;
    }


    buildAdHocColumns(): ColDef[] {
        this.gridFormatter.applyFilterKeyboardHintTooltips(this.columnDefs);
        return this.columnDefs;
    }

    buildLevel1Columns(): ColDef[] {
        let colDefs: ColDef[] = [];
        colDefs.push(this.submittedColumn);
        colDefs.push(this.dueDateColumn);
        colDefs.push(this.appNumberColumn);
        colDefs.push(this.companyColumn);
        colDefs.push(this.wellNameColumn);
        colDefs.push(this.wellNumberColumn);
        colDefs.push({
            headerName: "Assignee",
            headerTooltip: "Assignee",
            field: "assignedUserId",
            suppressFiltersToolPanel: true,
            suppressMovable: true,
            editable: true,
            cellEditor: 'agRichSelectCellEditor',
            cellEditorPopup: true,
            cellEditorParams: () => ({
                formatValue: (value: any) => value?.displayName ?? 'Unassigned',
                values: this.level1FilteredUsers
            }),
            cellRenderer: this.renderAssignedUser,
            valueGetter: (params) => this.level1FilteredUsers.find(u => u.id === params.data?.assignedUserId)
                ?? this.allUsers.find(u => u.id === params.data?.assignedUserId)
                ?? null,
            filterValueGetter: (params) => this.allUsers.find(u => u.id === params.data?.assignedUserId)?.displayName ?? 'Unassigned',
            valueSetter: this.assign,
            tooltipValueGetter: this.renderAssignedUser,
            maxWidth: 150,
            minWidth: 125,
            onCellValueChanged: this.cellValueChanged
        } as ColDef);
        colDefs.push(this.appTypeColumn);

        this.gridFormatter.applyFilterKeyboardHintTooltips(colDefs);
        return colDefs;
    }

    buildLevel2Columns(): ColDef[] {
        let colDefs: ColDef[] = [];
        colDefs.push(this.submittedColumn);
        colDefs.push(this.dueDateColumn);
        colDefs.push(this.appNumberColumn);
        colDefs.push(this.companyColumn);
        colDefs.push(this.wellNameColumn);
        colDefs.push(this.wellNumberColumn);
        colDefs.push({
            headerName: "Assignee",
            headerTooltip: "Assignee",
            field: "assignedUserId",
            suppressFiltersToolPanel: true,
            suppressMovable: true,
            editable: true,
            cellEditor: 'agRichSelectCellEditor',
            cellEditorPopup: true,
            cellEditorParams: () => ({
                formatValue: (value: any) => value?.displayName ?? 'Unassigned',
                values: this.level2FilteredUsers
            }),
            cellRenderer: this.renderAssignedUser,
            valueGetter: (params) => this.level2FilteredUsers.find(u => u.id === params.data?.assignedUserId)
                ?? this.allUsers.find(u => u.id === params.data?.assignedUserId)
                ?? null,
            filterValueGetter: (params) => this.allUsers.find(u => u.id === params.data?.assignedUserId)?.displayName ?? 'Unassigned',
            valueSetter: this.assign,
            tooltipValueGetter: this.renderAssignedUser,
            maxWidth: 150,
            minWidth: 125,
            onCellValueChanged: this.cellValueChanged
        } as ColDef);
        colDefs.push(this.appTypeColumn);

        this.gridFormatter.applyFilterKeyboardHintTooltips(colDefs);
        return colDefs;
    }

    buildLevel3Columns(): ColDef[] {
        let colDefs: ColDef[] = [];
        colDefs.push(this.submittedColumn);
        colDefs.push(this.dueDateColumn);
        colDefs.push(this.appNumberColumn);
        colDefs.push(this.companyColumn);
        colDefs.push(this.wellNameColumn);
        colDefs.push(this.wellNumberColumn);
        colDefs.push({
            headerName: "Assignee",
            headerTooltip: "Assignee",
            field: "assignedUserId",
            suppressFiltersToolPanel: true,
            suppressMovable: true,
            editable: true,
            cellEditor: 'agRichSelectCellEditor',
            cellEditorPopup: true,
            cellEditorParams: () => ({
                formatValue: (value: any) => value?.displayName ?? 'Unassigned',
                values: this.level3FilteredUsers
            }),
            cellRenderer: this.renderAssignedUser,
            valueGetter: (params) => this.level3FilteredUsers.find(u => u.id === params.data?.assignedUserId)
                ?? this.allUsers.find(u => u.id === params.data?.assignedUserId)
                ?? null,
            filterValueGetter: (params) => this.allUsers.find(u => u.id === params.data?.assignedUserId)?.displayName ?? 'Unassigned',
            valueSetter: this.assign,
            tooltipValueGetter: this.renderAssignedUser,
            maxWidth: 150,
            minWidth: 125,
            onCellValueChanged: this.cellValueChanged
        } as ColDef);
        colDefs.push(this.appTypeColumn);

        this.gridFormatter.applyFilterKeyboardHintTooltips(colDefs);
        return colDefs;
    }

    buildReturnedColumns(): ColDef[] {
        this.gridFormatter.applyFilterKeyboardHintTooltips(this.returnedPermitApplicationsColumns);
        return this.returnedPermitApplicationsColumns;
    }

    buildDenialColumns(): ColDef[] {
        this.gridFormatter.applyFilterKeyboardHintTooltips(this.pendingDenialColumns);
        return this.pendingDenialColumns;
    }

    dateFormatter(component: any, field: string) {
        return (obj) => {
            let returnValue = '';
            try {
                returnValue = obj.data[field] ? component.datePipe.transform(obj.data[field], 'MM/dd/yyyy') : '';
            }
            catch {
            }

            return returnValue;
        }
    }

    almostDueClass(component: PermitsWidgetComponent) {
        return (obj) => {
            return this.checkDueSoon(obj, component);
        }
    }

    cellValueChanged(params) : void{
        if (params.api) {
            params.api.refreshCells();
        }
    }

    checkDueSoon(obj, component: PermitsWidgetComponent) {
        if (obj.data.dueDate == null) return null;
        return component.dateService.isDueSoon(obj.data.dueDate, 3) ? 'almost-due' : null;
    }

    createdCellClassName(component: PermitsWidgetComponent) {
        return (obj) => {
            let rtnClassNames: Array<string> = []

            //pad left for inline swatch, leaves left space for vert alignment if not expedited
            rtnClassNames.push('no-swatch-pad-left');
			let expediteRequested = obj.data.isExpedited && (obj.data.isExpedited == true || obj.data.isExpedited.toLowerCase() == 'true');
			if (expediteRequested && this.expediteSettings != null && this.expediteSettings.length > 0) {
				let submitDate = new Date(obj.data.dateSubmitted);
				if (submitDate != null) {
					let settingAtTime = this.expediteSettings.find(s => new Date(s.validFrom) <= submitDate && new Date(s.validTo) > submitDate);
					if (settingAtTime != null) {
						expediteRequested = expediteRequested && settingAtTime.isActive;
					}
				}
			}
            //expedited?
            if (expediteRequested) {
                rtnClassNames.push('expedited');
            }

            //almost due?
            let almostDue = this.checkDueSoon(obj, component);

            if (almostDue != null) {
                rtnClassNames.push(almostDue);
            }
            return rtnClassNames;
        }
    }

    get isAdhoc(): boolean {
        return this.selectedItem.type === WorkOrderType.AdHoc;
    }

    onTabClicked(): void {
        setTimeout(() => {
            this.resizeGrid(this.adHocReviewGridApi);
            this.resizeGrid(this.returnedGridApi);
            this.resizeGrid(this.firstLevelReviewGridApi);
            this.resizeGrid(this.secondLevelReviewGridApi);
            this.resizeGrid(this.thirdLevelReviewGridApi);
        }, 50);
    }

    private resizeGrid(api?: GridApi): void {
        if (api && api.getDisplayedRowCount() > 0) {
            api.autoSizeAllColumns(false);
        }
    }

    getDataObs$(): Observable<[PendingFlow[], PendingFlow[], PendingFlow[], PendingFlow[], FlowIndex[], FlowIndex[], FlowIndex[]]> {
        this.showLoadingOverlays(true);

        let allUserSearchObs$ = this.groupService.get({ internal: true, stubs: false, fullContactDetails: true } as GroupSearchSpec);
        let level1ItemSearchObs$ = this.workOrderService.getPendingFlowsByTargetTransition("AllPermitLevel1WorkOrders").pipe(
            map((data: PendingFlow[]) => {
                return data.filter(d => d.applicationCompleteDate == null || d.applicationCompleteDate != '');
            }),
            switchMap((data: PendingFlow[]) => {
                const spec = {
                    applicationTypes: [ApplicationType.Stratigraphic],
                    applicationStates: [ApplicationState.InitialReview]
                } as MetadataSearchSpec;
                return this.applicationService.searchMetadata(spec).pipe(
                    map((meta: PendingFlow[]) => {
                        if (data == null) data = [];
                        return data.concat(meta.filter(m => this.authService.User.groups.includes(m.assignedGroupId) || this.authService.User.id == m.assignedUserId));
                    })
                )
            }),
            tap((data: PendingFlow[]) => {
                this.permitFirstLevelPendingFlows = this.sortExpeditedFirst(data);            
        }));
        let level2ItemSearchObs$ = this.workOrderService.getPendingFlowsByTargetTransition("AllPermitLevel2WorkOrders").pipe(
            map((data: PendingFlow[]) => {
                return data.filter(d => d.applicationCompleteDate == null || d.applicationCompleteDate != '');
            }),
            switchMap((data: PendingFlow[]) => {
                const spec = {
                    applicationTypes: [ApplicationType.Stratigraphic],
                    applicationStates: [ApplicationState.InitialApproval]
                } as MetadataSearchSpec;
                return this.applicationService.searchMetadata(spec).pipe(
                    map((meta: PendingFlow[]) => {
                        if (data == null) data = [];
                        return data.concat(meta.filter(m => this.authService.User.groups.includes(m.assignedGroupId) || this.authService.User.id == m.assignedUserId));
                    })
                )
            }),
            tap((data: PendingFlow[]) => {
                this.permitSecondLevelPendingFlows = this.sortExpeditedFirst(data);
        }));
        let level3ItemSearchObs$ = this.workOrderService.getPendingFlowsByTargetTransition("AllPermitLevel3WorkOrders").pipe(
            map((data: PendingFlow[]) => {
                return data.filter(d => d.applicationCompleteDate == null || d.applicationCompleteDate != '');
            }),
            switchMap((data: PendingFlow[]) => {
                const spec = {
                    applicationTypes: [ApplicationType.Stratigraphic],
                    applicationStates: [ApplicationState.FinalApproval]
                } as MetadataSearchSpec;
                return this.applicationService.searchMetadata(spec).pipe(
                    map((meta: PendingFlow[]) => {
                        if (data == null) data = [];
                        return data.concat(meta.filter(m => this.authService.User.groups.includes(m.assignedGroupId) || this.authService.User.id == m.assignedUserId));
                    })
                )
            }),
            tap((data: PendingFlow[]) => {
                this.permitThirdLevelPendingFlows = this.sortExpeditedFirst(data);
        }));
        let adHocSearchObs$ = this.workOrderService.getPendingFlowsByTargetTransition("AllPermitAdHocWorkOrders").pipe(
            map((data: PendingFlow[]) => {
                return data.filter(d => d.applicationCompleteDate == null || d.applicationCompleteDate != '');
            }),
            tap((data: PendingFlow[]) => {
                this.permitAdHocReviewPendingFlows = this.sortExpeditedFirst(data);
        }));
        let pendingDenialObs$ = this.flowInstanceService.queryFlowIndices("PendingDenialApplications", null).pipe(tap((result: FlowIndex[]) => {
            this.pendingDenialData = this.sortExpeditedFirst(result)
        }));
        let returnedObs$ = this.flowInstanceService.getReturnedIncompleteApplication("ReturnedPermitApplications", true).pipe(
            switchMap((data: FlowIndex[]) => {
                const spec = {
                    applicationTypes: [ApplicationType.Stratigraphic],
                    applicationStates: [ ApplicationState.Corrections ]
                } as MetadataSearchSpec;
                return this.applicationService.searchMetadata(spec).pipe(
                    map((meta: PendingFlow[]) => {
                        if (data == null) data = [];
                        return data.concat(meta);
                    })
                )
            }),
            tap((data: FlowIndex[]) => {
                this.returnedData = data;
            })
        );


        let incompleteObs$ = this.flowInstanceService.queryFlowIndices("IncompletePermitApplications").pipe(            
            switchMap((data: FlowIndex[]) => {
                var spec = {
                    applicationTypes: [ApplicationType.Stratigraphic],
                    applicationStates: [ApplicationState.VerifyCompletion]
                } as MetadataSearchSpec;
                return this.applicationService.searchMetadata(spec).pipe(
                    map((meta: FlowIndex[]) => {
                        if (data == null) data = [];
                        return data.concat(meta);
                    })
                )
            }),
            catchError((error) => {
                console.error(error);
                return of([]);
            })
        );

        let tempIncompletes: FlowIndex[] = [];
        let incomplete$ = incompleteObs$.pipe(
            map((incompletes: FlowIndex[]) => {
                return incompletes.filter(i => this.authService.User.groups.find(u => u === i.assignedGroupId) != null || this.authService.User.id === i.assignedUserId)
            }),
            mergeMap((incompletes: FlowIndex[]) => {
                tempIncompletes = incompletes;
                let searchCriteria = new PaymentSearchCriteria("", "", [PaymentStatus.Paid]);
                searchCriteria.applicationNumbers = incompletes.map(i => i.flowIdentifier);
                let obs$: Observable<ApplicationPayment[]> = of([]);
                if (searchCriteria.applicationNumbers.length > 0) {
                    obs$ = this.paymentService.search(searchCriteria);
                }
                return obs$.pipe(
                    map((pmts: ApplicationPayment[]) => {
                        this.payments = pmts;
                        this.incompletePermitApplications = tempIncompletes;
                        return incompletes;
                    })
                );
            }),
            
		);

		let settingsObs$: Observable<SystemSettingOverTime[]> = this._settings.searchOverTime({
			settingName: SettingNames.AllowExpedite
		} as SystemSettingSearchSpec).pipe(
			tap((settings: SystemSettingOverTime[]) => {
				this.expediteSettings = settings;
			}));

		let level1Subs$ =
			settingsObs$.pipe(
				switchMap(() => {
					return allUserSearchObs$.pipe(mergeMap((groupData) => {
						this.allUsers = [];
						this.level1FilteredUsers = [];
						this.level2FilteredUsers = [];
						this.level3FilteredUsers = [];
						if (groupData) {
							if (groupData instanceof Array) {
								this.groups = groupData as Group[];
								this.groups.forEach(g => this.allUsers = this.allUsers.concat(g.users));
							}
							this.allUsers = this.filterUniqueUsers(this.allUsers);
							this.allUsers.unshift({ id: "-1", displayName: "Unassigned" } as ContactStub);
						}
						return forkJoin([level1ItemSearchObs$, level2ItemSearchObs$, level3ItemSearchObs$, adHocSearchObs$, pendingDenialObs$, returnedObs$, incomplete$]);
				})
			)			
        }));
        return level1Subs$;
    }

    buildUserList(groups: Group[]): ContactStub[] {
        let userList: ContactStub[] = [];
        groups.forEach(u => userList = userList.concat(u.users));
        userList = this.filterUniqueUsers(userList);
        userList = this.sortUsers(userList);
        userList.unshift({ id: "-1", displayName: "Unassigned" } as ContactStub);
        return userList;
    }

    filterUniqueUsers(users: ContactStub[]): ContactStub[] {
        return users.filter((user, i, arr) => arr.findIndex(t => t.id === user.id) === i);
    }

    paidFieldFormatter(component: PermitsWidgetComponent) {
        return (obj) => {
            let fieldClass: string = "Unpaid";
            if (obj != null && obj.data != null) {
                let applicationNumber = obj.data.applicationNumber;
                if (applicationNumber != null) {
                    if (component.payments != null) {
                        let payment = component.payments.findIndex(p => p.applicationNumber == applicationNumber);
                        fieldClass = (payment != -1) ? "Paid" : "Unpaid";
                    }
                }
            }
            return fieldClass;
        }
    }

    refreshData(): void {
        let subs$ = this.getDataObs$().pipe(
            map(([level1Rows, level2Rows, level3Rows, adhocRows, denialRows, returnedRows, incompleteRows]) => {
                let level1Groups = this.groups.filter(g => this.groupHasPermission(g) && level1Rows.map(r => r.assignedGroupId).findIndex(r => r == g.id) != -1);
                let level2Groups = this.groups.filter(g => this.groupHasPermission(g) && level2Rows.map(r => r.assignedGroupId).findIndex(r => r == g.id) != -1);
                let level3Groups = this.groups.filter(g => this.groupHasPermission(g) && level3Rows.map(r => r.assignedGroupId).find(r => r == g.id) != null);
                this.level1FilteredUsers = this.buildUserList(level1Groups);
                this.level2FilteredUsers = this.buildUserList(level2Groups);
                this.level3FilteredUsers = this.buildUserList(level3Groups);
            })
        ).subscribe(() => {
            this.firstLevelReviewGridApi.setGridOption("columnDefs", []);
            this.firstLevelReviewGridApi.setGridOption("columnDefs", this.buildLevel1Columns());
            this.secondLevelReviewGridApi.setGridOption("columnDefs", []);
            this.secondLevelReviewGridApi.setGridOption("columnDefs", this.buildLevel2Columns());
            this.thirdLevelReviewGridApi.setGridOption("columnDefs", []);
            this.thirdLevelReviewGridApi.setGridOption("columnDefs", this.buildLevel3Columns());
        }, (error) => {
            console.error(error);
        });
        this.subscriptions.add(subs$);        
    }

    renderUserDropdownOptions(params: any): string {
        let returnValue = params && params.value && params.value.displayName ? params.value.displayName : "Unassigned";
        return returnValue;
    }

    renderAssignedUser(params: any): string {       
        let returnValue: string = "Unassigned";
        if (params && params.data && params.data.assignedUserId && params.data.assignedUserName) {
            returnValue = params.data.assignedUserName;
        }        
        return returnValue;
    }

    saveAssignments(event: any): void {
        let changedItem = <PendingFlow>event.data;
        if (event.context && event.context.componentParent) {
            const component = <PermitsWidgetComponent>event.context.componentParent;
            const pending = new PendingWorkOrderAssignment(changedItem.currentWorkItemId, changedItem.assignedUserId, changedItem.assignedUserName, true, changedItem.flowId);
            let updateSubs$ = component.workOrderService.assignWorkOrder(pending).pipe()
                .subscribe({
                   next:  (result) => { console.log("update managed"); },
                   error: (error) => { console.error(error); }
            });
            component.subscriptions.add(updateSubs$);
        }
        
    }

    setGridOptions(): void {
        this.firstLevelReviewGridOptions = <GridOptions>{
            columnDefs: this.buildLevel1Columns(),
            context: {
                componentParent: this
            },
            defaultColDef: { filter: true, resizable: true, sortable: true, editable: true } as ColDef,
            enableBrowserTooltips: false,
            tooltipTrigger: 'focus',
            onCellValueChanged: ($event) => this.saveAssignments($event),
            pagination: true,
            paginationAutoPageSize: true,
			suppressHorizontalScroll: false,
			onGridReady: (params) => { this.firstLevelReviewGridApi = params.api; },
            headerHeight: 50
        };

        this.secondLevelReviewGridOptions = <GridOptions>{
            columnDefs: this.buildLevel2Columns(),
            context: {
                componentParent: this
            },
            defaultColDef: { filter: true, resizable: true, sortable: true, editable: true } as ColDef,
            enableBrowserTooltips: false,
            tooltipTrigger: 'focus',
            onCellValueChanged: ($event) => this.saveAssignments($event),
            pagination: true,
            paginationAutoPageSize: true,
			suppressHorizontalScroll: false,
			onGridReady: (params) => { this.secondLevelReviewGridApi = params.api; },
            headerHeight: 50
        };

        this.thirdLevelReviewGridOptions = <GridOptions>{
            columnDefs: this.buildLevel3Columns(),
            context: {
                componentParent: this
            },
            defaultColDef: { filter: true, resizable: true, sortable: true, editable: true } as ColDef,
            enableBrowserTooltips: false,
            tooltipTrigger: 'focus',
            onCellValueChanged: ($event) => this.saveAssignments($event),
            pagination: true,
            paginationAutoPageSize: true,
			suppressHorizontalScroll: false,
			onGridReady: (params) => { this.thirdLevelReviewGridApi = params.api; },
            headerHeight: 50
        };

        this.adHocReviewGridOptions = <GridOptions>{
            columnDefs: this.buildAdHocColumns(),
            defaultColDef: { filter: true, resizable: true, sortable: true } as ColDef,
            enableBrowserTooltips: false,
            tooltipTrigger: 'focus',
            pagination: true,
            paginationAutoPageSize: true,
			suppressHorizontalScroll: false,
			onGridReady: (params) => {
				this.adHocReviewGridApi = params.api;
			},
            headerHeight: 50
        };

        this.pendingDenialGridOptions = <GridOptions>{
            columnDefs: this.buildDenialColumns(),
            defaultColDef: { filter: true, resizable: true, sortable: true } as ColDef,
            enableBrowserTooltips: false,
            tooltipTrigger: 'focus',
            pagination: true,
            paginationAutoPageSize: true,
			suppressHorizontalScroll: false,
			onGridReady: (params) => {
				this.pendingDenialGridApi = params.api;
				// this.pendingDenialGridApi.sizeColumnsToFit();
			},
            headerHeight: 50
        };

        this.returnedGridOptions = <GridOptions>{
            columnDefs: this.buildReturnedColumns(),
            defaultColDef: { filter: true, resizable: true, sortable: true } as ColDef,
            enableBrowserTooltips: false,
            tooltipTrigger: 'focus',
            pagination: true,
            paginationAutoPageSize: true,
			suppressHorizontalScroll: false,
			onGridReady: (params) => {
				this.returnedGridApi = params.api;
				// this.returnedGridApi.sizeColumnsToFit();
			},
            headerHeight: 50
        };

        this.gridFormatter.applyFilterKeyboardHintTooltips(this.submittedIncompleteApplicationsColumns);
        this.incompleteGridOptions = <GridOptions>{
            columnDefs: this.submittedIncompleteApplicationsColumns,
            context: {
                componentParent: this
            },
            defaultColDef: { filter: true, resizable: true, sortable: true, editable: true } as ColDef,
            enableBrowserTooltips: false,
            tooltipTrigger: 'focus',
            onCellValueChanged: ($event) => this.saveAssignments($event),
            pagination: true,
            paginationAutoPageSize: true,
			suppressHorizontalScroll: false,
			onGridReady: (params) => {
				this.incompleteGridApi = params.api;
			},
            headerHeight: 50
        };
    }

    showLoadingOverlays(show: boolean): void {
        if (this.firstLevelReviewGridOptions && this.firstLevelReviewGridApi) {
            if (show) {
                this.firstLevelReviewGridApi.setGridOption("rowData",[]);
                this.firstLevelReviewGridApi.setGridOption("loading", true);
            } else
                this.firstLevelReviewGridApi.setGridOption("loading", false);
        }

        if (this.secondLevelReviewGridOptions && this.secondLevelReviewGridApi) {
            if (show) {
                this.secondLevelReviewGridApi.setGridOption("rowData",[]);
                this.secondLevelReviewGridApi.setGridOption("loading", true);
            }
            else
                this.secondLevelReviewGridApi.setGridOption("loading", false);
        }

        if (this.thirdLevelReviewGridOptions && this.thirdLevelReviewGridApi) {
            if (show) {
                this.thirdLevelReviewGridApi.setGridOption("rowData",[]);
                this.thirdLevelReviewGridApi.setGridOption("loading", true);
            }
            else
                this.thirdLevelReviewGridApi.setGridOption("loading", false);
        }

        if (this.adHocReviewGridOptions && this.adHocReviewGridApi) {
            if (show) {
                this.adHocReviewGridApi.setGridOption("rowData",[]);
                this.adHocReviewGridApi.setGridOption("loading", true);
            }
            else
                this.adHocReviewGridApi.setGridOption("loading", false);
        }

        if (this.pendingDenialGridOptions && this.pendingDenialGridApi) {
            if (show) {
                this.pendingDenialGridApi.setGridOption("rowData",[]);
                this.pendingDenialGridApi.setGridOption("loading", true);
            }
            else
                this.pendingDenialGridApi.setGridOption("loading", false);
        }

        if (this.returnedGridOptions && this.returnedGridApi) {
            if (show) {
                this.returnedGridApi.setGridOption("rowData",[]);
                this.returnedGridApi.setGridOption("loading", true);
            }
            else
                this.returnedGridApi.setGridOption("loading", false);
        }
    }

    sortExpeditedFirst(data: FlowIndex[]): FlowIndex[] {
        const expedited = data.filter(item => item["isExpedited"] != null && new String(item["isExpedited"]).length > 0 && new Boolean(item["isExpedited"]) == true)
            .sort((a, b) => (a["dateSubmitted"] && b["dateSubmitted"])
                ? (a["dateSubmitted"] < b["dateSubmitted"])
                    ? -1
                    : 1 
                : 0);
        const nonExpedited = data.filter(item => !item["isExpedited"] || new String(item["isExpedited"]).length == 0 || new Boolean(item["isExpedited"]) != true)
            .sort((a, b) => (a["dateSubmitted"] && b["dateSubmitted"])
                ? (a["dateSubmitted"] < b["dateSubmitted"])
                    ? -1
                    : 1
                : 0);
        const result = expedited.concat(nonExpedited);
        return result;
    }

    sortUsers(users: ContactStub[]): ContactStub[] {
        return users.sort((a, b) => {
            if (a.lastName > b.lastName) return 1;
            if (a.lastName < b.lastName) return -1;
            if (a.lastName == b.lastName) {
                if (a.firstName > b.firstName) return 1;
                if (a.firstName < b.firstName) return -1;
            }
            return 0;
        })
    }

    statusFormatter(component) {
        return (obj) => 'Pending';
    }

    get user(): UserIdentity {
        return this.authService.User;
    }

    get workOrderCreator(): Contact {
        var contact = new Contact();
        if (!this.selectedItem.creator)
            contact.firstName = 'System';
        else
            contact = this.selectedItem.creator;
        return contact;
    }

    enableFlowIndexApplicationNumberLink() {
        return (obj) => {
            let flowIndex = obj as FlowIndex;
            return (this.authService.User.groups.filter(a => a == flowIndex.assignedGroupId).length > 0);
        }
    }
}

